import React, { ComponentProps, ReactNode, useEffect, useMemo } from 'react';
import Link from '../../Router/Link';
import Skeleton from '../../components-legacy/Skeleton';
import ViewportLoader from '../../components-legacy/ViewportLoader';
import Container from '../../styled/Container';
import { SrOnly } from '../../styled/SrOnly/wrappers';
import { ChevronUpIcon, ChevronDownIcon } from '../../styled/Icon';

import {
  FacebookIcon,
  InstagramIcon,
  PinterestIcon,
  MailIcon,
  StoreIcon,
  PhoneIcon,
  UserIcon,
} from '../../styled/Icon';

import {
  SiteFooter,
  Footer as StyledFooter,
  FooterSection,
  List,
  Item,
  LinkGroup as StyledLinkGroup,
  LinkGroupHeader,
  LinkGroupHeading,
  LinkGroupIcon,
  LinkGroupBody,
  LinkGroupItem as StyledLinkGroupItem,
  Link as FooterLink,
  Bottom,
  BottomItem,
  SocialList,
  SocialItem,
  IconLink,
  IconLinkIcon,
  IconLinkText,
  Store,
} from '../../styled/SiteFooter/wrappers';

import useToggle from '../../hooks/useToggle';
import { useTrackGenericEvent } from '../../hooks/tracking/useTrackingEvents';

const NearbyStores = React.lazy(() => import('../../components-legacy/NearbyStores'));

const resolveIcon = (icon: string): JSX.Element | null => {
  switch (icon) {
    case 'icon-store':
      return <StoreIcon />;
    case 'icon-headset':
      return <PhoneIcon />;
    case 'icon-user':
      return <UserIcon />;
    case 'icon-facebook-solid':
      return <FacebookIcon />;
    case 'icon-instagram':
      return <InstagramIcon />;
    case 'icon-pinterest-solid':
      return <PinterestIcon />;
    case 'icon-envelope-solid':
      return <MailIcon />;
    default:
      return null;
  }
};

interface ILinkGroup {
  items: ILink[];
  title: string;
}

const LinkGroup = ({ items, title }: ILinkGroup) => {
  const [open, toggleOpen] = useToggle(false);
  return (
    <StyledLinkGroup>
      <LinkGroupHeader onClick={toggleOpen} title={`Visa länkar under kategorin ${title}`}>
        <LinkGroupHeading>{title}</LinkGroupHeading>
        <LinkGroupIcon>
          {open && <ChevronUpIcon strokeWidth="2" className="xlarge" />}
          {!open && <ChevronDownIcon strokeWidth="2" className="xlarge" />}
        </LinkGroupIcon>
      </LinkGroupHeader>
      <LinkGroupBody className={(open && 'is-open') || undefined}>
        {items.map((item) => (
          <LinkGroupItem key={item.path + item.title} {...item} />
        ))}
      </LinkGroupBody>
    </StyledLinkGroup>
  );
};

const LinkGroupItem = ({ path, title }: ILink) => {
  const trackingEvent = useMemo(() => ({ event: 'footer_item_click', value: title }), [title]);
  const track = useTrackGenericEvent(trackingEvent);

  return (
    <StyledLinkGroupItem key={path}>
      <Link Component={FooterLink} href={path} title={title} onBeforeNavigate={track}>
        {title}
      </Link>
    </StyledLinkGroupItem>
  );
};

interface IFooter {
  links: ILink[];
  navigation: Record<string, ILink[]>;
  socialLinks: ILink[];
}

const Footer = ({ links, navigation, socialLinks }: IFooter) => {
  useEffect(() => {
    const script = document.createElement('script');
    script.src =
      'https://cert.tryggehandel.net/js/script.js?id=6a756c48-05d8-4e4f-8759-487523d601d2';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      // clean up the script when the component in unmounted
      document.body.removeChild(script);
    };
  }, []);

  return (
    <SiteFooter>
      <Store>
        <Container>
          <ViewportLoader loader={<Skeleton height="385px" />}>
            <NearbyStores />
          </ViewportLoader>
        </Container>
      </Store>
      <StyledFooter>
        <Container>
          <FooterSection>
            <List className="spacing-ltmd">
              {links?.map((link) => (
                <Item key={link.path}>
                  <FooterItemLink
                    {...link}
                    Component={IconLink}
                    href={link.path}
                    title={link.title}>
                    <IconLinkIcon>{resolveIcon(link.icon)}</IconLinkIcon>
                    <IconLinkText>{link.title}</IconLinkText>
                  </FooterItemLink>
                </Item>
              ))}
            </List>
          </FooterSection>
          <FooterSection className="separator-top">
            <List className="link-groups">
              {navigation &&
                Object.keys(navigation).map((title) => (
                  <Item key={title}>
                    <LinkGroup title={title} items={navigation[title]} />
                  </Item>
                ))}
            </List>
          </FooterSection>

          <FooterSection>
            <Bottom>
              <BottomItem>
                <div className="teh-certificate">
                  <img
                    src="https://cert.tryggehandel.net/js/logo.svg"
                    alt="Trygg e-handel"
                    loading="lazy"
                    width="60px"
                    height="60px"
                    data-cmp-info="9"
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </BottomItem>
              <BottomItem className="pos-right">
                <SocialList>
                  {socialLinks?.map((link) => (
                    <SocialItem key={link.path}>
                      <FooterItemLink
                        {...link}
                        href={link.path}
                        title={link.title}
                        target="_blank"
                        rel="noopener noreferrer">
                        <IconLinkIcon>
                          {resolveIcon(link.icon)}
                          <SrOnly>{link.title}</SrOnly>
                        </IconLinkIcon>
                      </FooterItemLink>
                    </SocialItem>
                  ))}
                </SocialList>
              </BottomItem>
            </Bottom>
          </FooterSection>
        </Container>
      </StyledFooter>
    </SiteFooter>
  );
};

export default Footer;

interface IFooterItemLink extends ILink {
  children: ReactNode;
}

const FooterItemLink = ({
  children,
  path,
  title,
  ...props
}: IFooterItemLink & ComponentProps<typeof Link>) => {
  const trackingEvent = useMemo(
    () => ({ event: 'footer_item_click', value: title || path }),
    [path, title],
  );

  const track = useTrackGenericEvent(trackingEvent);

  return (
    <Link Component={IconLink} href={path} title={title} onBeforeNavigate={track} {...props}>
      {children}
    </Link>
  );
};
